import { useTheme } from '@emotion/react';
import { OptionallyVisible } from 'components';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import IconCopy from 'components/icons/copy';
import IconMobilePhoneWithSparkles from 'components/icons/mobilePhoneWithSparkles';
import Link from 'components/link';

import { BridgeKycStatusBadge } from 'pages/cryptoPayout/components/kycStatusBadge';

import RoutePath from 'router/path';

import { BridgeKycStatus, CryptoPayoutStatus } from 'types/cryptoPayout';

import LABELS, { KEYS } from './keys';
import {
  Content,
  FlexRow,
  Grid,
  Header,
  Information,
  Label,
  LinkOverflowWrapper,
  QRCodePanel,
  StyledCopyButton,
  StyledIconCheckCircleOutline,
  StyledLink,
  StyledPanel,
} from './styles';

interface CryptoPayoutInfoProps {
  token: string;
  cryptoPayoutId: string;
  customerStatus?: BridgeKycStatus;
  payoutStatus: CryptoPayoutStatus;
}

const CryptoPayoutInfo = ({ token, cryptoPayoutId, customerStatus, payoutStatus }: CryptoPayoutInfoProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const payoutConfirmedStatuses = [CryptoPayoutStatus.Confirmed, CryptoPayoutStatus.Completed];
  const isPayoutConfirmed = payoutConfirmedStatuses.includes(payoutStatus);

  const createCheckoutUrl = () => {
    const checkoutUrl = `${window.s4cConfig.corWidgetUrl}pay/${cryptoPayoutId}/crypto-payout`;

    return new URL(checkoutUrl);
  };

  const createCheckoutUrlWithToken = (token: string) => {
    const targetUrl = createCheckoutUrl();
    targetUrl.searchParams.set('token', token);
    return targetUrl;
  };

  const urlWithToken = createCheckoutUrlWithToken(token);

  return (
    <StyledPanel>
      <Header>{t(LABELS.HEADER)}</Header>
      <Content>
        <Information>
          <IconMobilePhoneWithSparkles />
          {t(LABELS.INSTRUCTION)}
        </Information>
        <QRCodePanel>
          <OptionallyVisible visible={!isPayoutConfirmed}>
            <QRCode
              value={urlWithToken.toString()}
              size={KEYS.QR_CODE_SIZE}
              fgColor={theme.colors.black}
              bgColor={theme.colors.background}
            />
          </OptionallyVisible>
          <OptionallyVisible visible={isPayoutConfirmed}>
            <StyledIconCheckCircleOutline width={140} height={140} />
          </OptionallyVisible>
        </QRCodePanel>
        <Grid>
          <Label>{t(LABELS.LINK_LABEL)}</Label>
          <FlexRow>
            <LinkOverflowWrapper>
              <Link target="_blank" to={urlWithToken.toString()} decorated>
                {createCheckoutUrl().toString()}
              </Link>
            </LinkOverflowWrapper>
            <StyledCopyButton flat value={urlWithToken.toString()}>
              <IconCopy />
            </StyledCopyButton>
          </FlexRow>
          <Label>{t(LABELS.ID_LABEL)}</Label>
          <StyledLink target="_blank" to={RoutePath.cryptoPayout.byId(cryptoPayoutId)} decorated>
            {cryptoPayoutId}
          </StyledLink>
          <OptionallyVisible visible={Boolean(customerStatus)}>
            <Label>{t(LABELS.KYC_STATUS_LABEL)}</Label>
            <span>
              <BridgeKycStatusBadge status={customerStatus!} />
            </span>
          </OptionallyVisible>
        </Grid>
      </Content>
    </StyledPanel>
  );
};

export default CryptoPayoutInfo;
